import type { TimelineDuration } from '@atlassian/jira-software-roadmap-timeline-table/src/common/types/timeline.tsx';
import type {
	TimelineViewIssue,
	UpdateIssueField,
	ScheduleIssueField,
} from '../../../services/get-timeline-view-issues/types.tsx';
import type { JiraDatesUpdate } from '../types.tsx';
import { toISOString, getSafeScheduleDates } from '../utils.tsx';

export const shouldScheduleChildrenIssues = ({
	issue,
	modifiedDates: { startDate, dueDate },
}: {
	issue: TimelineViewIssue;
	modifiedDates: JiraDatesUpdate;
}) => {
	/**
	 * When the issue is a parent
	 * When parent bar is dragged (i.e. both start and due dates are updated), then child bars also dragged together.
	 * When parent bar is resized (i.e. both start and due dates are updated by Edit dependency modal),then child bars also resized together.
	 * When parent bar is resized (i.e. only one of start of due dates are updated), then child bars are unaffected.
	 */

	const { startDate: oldStartDate, dueDate: oldDueDate, hasChildren } = issue;
	const isBothDatesPopulated =
		startDate !== undefined &&
		dueDate !== undefined &&
		startDate.date !== null &&
		dueDate.date !== null;
	const isBothDatesUpdated = startDate?.date !== oldStartDate && dueDate?.date !== oldDueDate;

	return hasChildren && isBothDatesPopulated && isBothDatesUpdated;
};

/*
 * This calculates the dates offset
 */

export const getIssuesBulkDatesUpdate = ({
	issue,
	issueChildren,
	dates: { startDate, dueDate },
	timelineDuration,
}: {
	issue: TimelineViewIssue;
	issueChildren: TimelineViewIssue[];
	dates: JiraDatesUpdate;
	timelineDuration: TimelineDuration;
}) => {
	const bulkUpdates: ScheduleIssueField[] = [];
	const originalValues: UpdateIssueField[] = [];

	if (
		issueChildren.length &&
		startDate !== undefined &&
		startDate.date !== null &&
		dueDate !== undefined &&
		dueDate.date !== null
	) {
		let offset = 0;
		const oldStartDate = issue.startDate ?? null;
		const oldDueDate = issue.dueDate ?? null;

		if (oldStartDate != null) {
			offset = startDate.date - oldStartDate;
		} else if (oldDueDate != null) {
			offset = dueDate.date - oldDueDate;
		}

		if (offset) {
			issueChildren.forEach((issueChild) => {
				const { startDate: childStartDate, dueDate: childDueDate } = issueChild;

				const { startDate: safeStartDate, dueDate: safeDueDate } = getSafeScheduleDates({
					startDate: childStartDate,
					dueDate: childDueDate,
					timelineDuration,
					offset,
				});

				const newStartDate = safeStartDate != null ? toISOString(safeStartDate) : null;
				const newDueDate = safeDueDate != null ? toISOString(safeDueDate) : null;

				// Exclude issue if the dates have no changes
				if (childStartDate !== safeStartDate || childDueDate !== safeDueDate) {
					originalValues.push({
						id: issueChild.issueId,
						fields: {
							startDate: childStartDate ? toISOString(childStartDate) : null,
							dueDate: childDueDate ? toISOString(childDueDate) : null,
						},
					});
					bulkUpdates.push({
						id: issueChild.issueId,
						fields: {
							startDate: {
								date: newStartDate,
							},
							dueDate: {
								date: newDueDate,
							},
						},
					});
				}
			});
		}
	}

	return { bulkUpdates, originalValues };
};
